import { Class, Payment, Student } from '../types/firestore';
import { Assignment } from '../types/index';
import { advancedAttendanceService } from './advancedAttendanceService';
import {
  classesService,
  paymentsService,
  studentsService,
} from './firestoreService';
import teacherActionsService from './teacherActionsService';

export interface AttendanceAnalytics {
  studentId: string;
  studentName: string;
  totalSessions: number;
  attendedSessions: number;
  absentSessions: number;
  lateCount: number;
  attendanceRate: number;
  attendanceTrend: { date: string; status: string }[];
}

export interface GradeAnalytics {
  studentId: string;
  studentName: string;
  subject?: string;
  assignments: Assignment[];
  averageGrade: number;
  maxGrade: number;
  minGrade: number;
  improvementTrend: { date: string; grade: number; assignment: string }[];
  comparisonToClass?: number;
}

export interface ClassPerformanceAnalytics {
  classId: string;
  className: string;
  subject: string;
  totalStudents: number;
  averageAttendance: number;
  averageGrade: number;
  topPerformers: { studentId: string; name: string; average: number }[];
  strugglingStudents: { studentId: string; name: string; average: number }[];
  attendanceTrend: { month: string; percentage: number }[];
  gradeTrend: { month: string; average: number }[];
}

export interface TeacherAnalytics {
  totalStudents: number;
  totalClasses: number;
  totalSessions: number;
  overallAttendanceRate: number;
  overallAverageGrade: number;
  monthlyRevenue: { month: string; amount: number; target?: number }[];
  studentGrowth: { month: string; count: number }[];
  performanceBySubject: {
    subject: string;
    avgGrade: number;
    attendance: number;
  }[];
}

export interface AssignmentStudentReportRow {
  assignmentId: string;
  assignmentTitle: string;
  classId: string;
  className: string;
  subject: string;
  studentId: string;
  studentName: string;
  status: 'missing' | 'submitted' | 'late' | 'graded' | 'returned';
  submittedAt?: Date;
  isLate: boolean;
  grade?: number;
  totalPoints: number;
  scorePercent?: number;
  feedback?: string;
}

export interface AssignmentAnalytics {
  assignmentId: string;
  title: string;
  classId: string;
  className: string;
  subject: string;
  dueDate: Date;
  totalPoints: number;
  targetCount: number;
  submittedCount: number;
  missingCount: number;
  lateCount: number;
  gradedCount: number;
  submissionRate: number;
  lateRate: number;
  averageScorePercent: number;
  minScorePercent: number;
  maxScorePercent: number;
  missingStudents: { studentId: string; studentName: string }[];
  studentRows: AssignmentStudentReportRow[];
}

export interface ReportFilters {
  startDate?: Date;
  endDate?: Date;
  subjectFilter?: string;
  classFilter?: string;
  studentFilter?: string[];
  gradeThreshold?: number;
  attendanceThreshold?: number;
}

const toDate = (value: any): Date => {
  if (value && typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value || Date.now());
};

const average = (values: number[]): number =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : 0;

const monthBuckets = () =>
  Array.from({ length: 6 }, (_, index) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - (5 - index));
    return {
      month: date.toLocaleDateString('ar-SA', {
        month: 'short',
        year: 'numeric',
      }),
      start: new Date(date.getFullYear(), date.getMonth(), 1),
      end: new Date(date.getFullYear(), date.getMonth() + 1, 1),
    };
  });

const percent = (part: number, total: number): number =>
  total > 0 ? Math.round((part / total) * 1000) / 10 : 0;

const scorePercent = (grade: unknown, totalPoints: number): number | undefined => {
  const numericGrade = Number(grade);
  if (!Number.isFinite(numericGrade) || totalPoints <= 0) return undefined;
  return Math.round((numericGrade / totalPoints) * 1000) / 10;
};

const csvCell = (value: unknown): string => {
  const text =
    value instanceof Date
      ? value.toLocaleString('ar-EG')
      : value === undefined || value === null
        ? ''
        : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

export class AdvancedReportsService {
  async getStudentAttendanceAnalytics(
    teacherId: string,
    filters: ReportFilters = {}
  ): Promise<AttendanceAnalytics[]> {
    const students = await studentsService.getByTeacherId(teacherId);
    const analytics: AttendanceAnalytics[] = [];

    for (const student of students) {
      if (filters.studentFilter && !filters.studentFilter.includes(student.id)) {
        continue;
      }

      const attendanceRecords =
        await advancedAttendanceService.getAttendanceReport(
          teacherId,
          filters.startDate || new Date(0),
          filters.endDate || new Date(),
          student.id
        );

      const totalSessions = attendanceRecords.length;
      const attendedSessions = attendanceRecords.filter(
        record => record.status === 'present'
      ).length;
      const absentSessions = attendanceRecords.filter(
        record => record.status === 'absent'
      ).length;
      const lateCount = attendanceRecords.filter(
        record => record.status === 'late'
      ).length;
      const attendanceRate =
        totalSessions > 0 ? (attendedSessions / totalSessions) * 100 : 0;

      if (
        filters.attendanceThreshold &&
        attendanceRate < filters.attendanceThreshold
      ) {
        continue;
      }

      analytics.push({
        studentId: student.id,
        studentName: student.name,
        totalSessions,
        attendedSessions,
        absentSessions,
        lateCount,
        attendanceRate,
        attendanceTrend: attendanceRecords.slice(0, 30).map(record => ({
          date: toDate(record.date).toLocaleDateString('ar-SA'),
          status: record.status,
        })),
      });
    }

    return analytics;
  }

  async getStudentGradeAnalytics(
    teacherId: string,
    filters: ReportFilters = {}
  ): Promise<GradeAnalytics[]> {
    const [students, assignments, submissions] = await Promise.all([
      studentsService.getByTeacherId(teacherId),
      teacherActionsService.getTeacherAssignments(teacherId, 500),
      teacherActionsService.getTeacherSubmissions(teacherId, 1000),
    ]);
    const filteredAssignments = assignments.filter(
      assignment =>
        !filters.subjectFilter || assignment.subject === filters.subjectFilter
    );

    return students
      .filter(
        student =>
          !filters.studentFilter || filters.studentFilter.includes(student.id)
      )
      .map(student => {
        const grades: number[] = [];
        const improvementTrend: {
          date: string;
          grade: number;
          assignment: string;
        }[] = [];

        filteredAssignments.forEach(assignment => {
          submissions
            .filter(
              submission =>
                submission.assignmentId === assignment.id &&
                submission.studentId === student.id &&
                submission.grade !== undefined
            )
            .forEach(submission => {
              const maxGrade =
                assignment.maxGrade || assignment.totalPoints || 100;
              const gradePercentage = (submission.grade / maxGrade) * 100;
              grades.push(gradePercentage);
              improvementTrend.push({
                date: toDate(submission.submittedAt).toLocaleDateString(
                  'ar-SA'
                ),
                grade: gradePercentage,
                assignment: assignment.title,
              });
            });
        });

        return {
          studentId: student.id,
          studentName: student.name,
          subject: filters.subjectFilter,
          assignments: filteredAssignments as Assignment[],
          averageGrade: average(grades),
          maxGrade: grades.length > 0 ? Math.max(...grades) : 0,
          minGrade: grades.length > 0 ? Math.min(...grades) : 0,
          improvementTrend: improvementTrend.slice(0, 10),
        };
      })
      .filter(
        analytics =>
          analytics.averageGrade > 0 &&
          (!filters.gradeThreshold ||
            analytics.averageGrade >= filters.gradeThreshold)
      );
  }

  async getClassPerformanceAnalytics(
    teacherId: string,
    classId?: string,
    filters: ReportFilters = {}
  ): Promise<ClassPerformanceAnalytics[]> {
    const [classes, students, attendanceAnalytics, gradeAnalytics] =
      await Promise.all([
        classesService.getByTeacherId(teacherId),
        studentsService.getByTeacherId(teacherId),
        this.getStudentAttendanceAnalytics(teacherId, filters),
        this.getStudentGradeAnalytics(teacherId, filters),
      ]);
    const studentMap = new Map(students.map(student => [student.id, student]));
    const attendanceMap = new Map(
      attendanceAnalytics.map(item => [item.studentId, item.attendanceRate])
    );
    const gradeMap = new Map(
      gradeAnalytics.map(item => [item.studentId, item.averageGrade])
    );

    return classes
      .filter(item => !classId || item.id === classId)
      .filter(
        item => !filters.subjectFilter || item.subject === filters.subjectFilter
      )
      .map(classData => {
        const classStudents = (classData.enrolledStudents || [])
          .map(studentId => studentMap.get(studentId))
          .filter((student): student is Student => Boolean(student));
        const performanceData = classStudents.map(student => ({
          studentId: student.id,
          name: student.name,
          average: gradeMap.get(student.id) || student.averageGrade || 0,
        }));
        performanceData.sort((a, b) => b.average - a.average);

        return {
          classId: classData.id,
          className: classData.name,
          subject: classData.subject,
          totalStudents: classStudents.length,
          averageAttendance: average(
            classStudents.map(student => attendanceMap.get(student.id) || 0)
          ),
          averageGrade: average(
            classStudents.map(student => gradeMap.get(student.id) || 0)
          ),
          topPerformers: performanceData.slice(0, 3),
          strugglingStudents: performanceData
            .filter(student => student.average < 70)
            .slice(-3),
          attendanceTrend: [],
          gradeTrend: [],
        };
      });
  }

  async getTeacherAnalytics(
    teacherId: string,
    filters: ReportFilters = {}
  ): Promise<TeacherAnalytics> {
    const [students, classes, sessions, payments, attendanceAnalytics] =
      await Promise.all([
        studentsService.getByTeacherId(teacherId),
        classesService.getByTeacherId(teacherId),
        teacherActionsService.getTeacherSessions(teacherId, 1000),
        paymentsService.getByTeacherId(teacherId),
        this.getStudentAttendanceAnalytics(teacherId, filters),
      ]);

    const buckets = monthBuckets();
    const monthlyRevenue = buckets.map(bucket => ({
      month: bucket.month,
      amount: payments
        .filter(payment => payment.status === 'paid')
        .filter((payment: Payment) => {
          const date = toDate(payment.paidAt || payment.createdAt);
          return date >= bucket.start && date < bucket.end;
        })
        .reduce((sum, payment) => sum + payment.amount, 0),
    }));

    const studentGrowth = buckets.map(bucket => ({
      month: bucket.month,
      count: students.filter(student => toDate(student.createdAt) < bucket.end)
        .length,
    }));

    const attendanceMap = new Map(
      attendanceAnalytics.map(item => [item.studentId, item.attendanceRate])
    );

    return {
      totalStudents: students.length,
      totalClasses: classes.length,
      totalSessions: sessions.length,
      overallAttendanceRate: average(
        attendanceAnalytics.map(item => item.attendanceRate)
      ),
      overallAverageGrade: average(
        students.map(student => student.averageGrade || 0).filter(Boolean)
      ),
      monthlyRevenue,
      studentGrowth,
      performanceBySubject: classes.map((classData: Class) => {
        const classStudents = students.filter(student =>
          (classData.enrolledStudents || []).includes(student.id)
        );
        return {
          subject: classData.subject || classData.name,
          avgGrade: average(
            classStudents.map(student => student.averageGrade || 0)
          ),
          attendance: average(
            classStudents.map(student => attendanceMap.get(student.id) || 0)
          ),
        };
      }),
    };
  }

  async getAssignmentAnalytics(
    teacherId: string,
    filters: ReportFilters = {}
  ): Promise<AssignmentAnalytics[]> {
    const [students, classes, assignments, submissions] = await Promise.all([
      studentsService.getByTeacherId(teacherId),
      classesService.getByTeacherId(teacherId),
      teacherActionsService.getTeacherAssignments(teacherId, 500),
      teacherActionsService.getTeacherSubmissions(teacherId, 5000),
    ]);
    const studentNames = new Map(
      students.map(student => [student.id, student.name || 'طالب غير محدد'])
    );
    const classNames = new Map(classes.map(item => [item.id, item.name]));
    const submissionsByAssignmentStudent = new Map(
      submissions.map(submission => [
        `${submission.assignmentId}:${submission.studentId}`,
        submission,
      ])
    );
    const now = new Date();

    return assignments
      .filter(
        assignment =>
          !filters.classFilter || assignment.classId === filters.classFilter
      )
      .filter(
        assignment =>
          !filters.subjectFilter || assignment.subject === filters.subjectFilter
      )
      .filter(assignment => {
        const dueDate = toDate(assignment.dueDate);
        return (
          (!filters.startDate || dueDate >= filters.startDate) &&
          (!filters.endDate || dueDate <= filters.endDate)
        );
      })
      .map(assignment => {
        const dueDate = toDate(assignment.dueDate);
        const normalizedTotalPoints = Number(
          assignment.totalPoints || assignment.maxGrade || 100
        );
        const totalPoints =
          Number.isFinite(normalizedTotalPoints) && normalizedTotalPoints > 0
            ? normalizedTotalPoints
            : 100;
        const targetStudentIds: string[] = Array.isArray(assignment.studentIds)
          ? assignment.studentIds.filter(
              (studentId: unknown): studentId is string =>
                typeof studentId === 'string'
            )
          : [];
        const scopedStudentIds = filters.studentFilter
          ? targetStudentIds.filter((studentId: string) =>
              filters.studentFilter?.includes(studentId)
            )
          : targetStudentIds;
        const allowLate =
          assignment.allowLateSubmissions ??
          assignment.allowLateSubmission ??
          false;

        const studentRows: AssignmentStudentReportRow[] = scopedStudentIds.map(
          (studentId: string) => {
          const submission = submissionsByAssignmentStudent.get(
            `${assignment.id}:${studentId}`
          );
          const submittedAt = submission?.submittedAt
            ? toDate(submission.submittedAt)
            : undefined;
          const isLate = Boolean(
            submission?.isLate || (submittedAt && submittedAt > dueDate)
          );
          const calculatedScorePercent = scorePercent(
            submission?.grade,
            totalPoints
          );
          const missing = !submission && dueDate < now && !allowLate;
          const status: AssignmentStudentReportRow['status'] = submission
            ? submission.status === 'graded'
              ? 'graded'
              : submission.status === 'returned'
                ? 'returned'
                : isLate
                  ? 'late'
                  : 'submitted'
            : missing
              ? 'missing'
              : 'missing';

          return {
            assignmentId: assignment.id,
            assignmentTitle: assignment.title || 'واجب بدون عنوان',
            classId: assignment.classId || '',
            className:
              assignment.className ||
              classNames.get(assignment.classId) ||
              assignment.subject ||
              'فصل غير محدد',
            subject: assignment.subject || '',
            studentId,
            studentName: studentNames.get(studentId) || 'طالب غير محدد',
            status,
            submittedAt,
            isLate,
            grade: submission?.grade,
            totalPoints,
            scorePercent: calculatedScorePercent,
            feedback: submission?.feedback,
          };
          }
        );
        const scoredRows = studentRows.filter(
          row => row.scorePercent !== undefined
        );

        return {
          assignmentId: assignment.id,
          title: assignment.title || 'واجب بدون عنوان',
          classId: assignment.classId || '',
          className:
            assignment.className ||
            classNames.get(assignment.classId) ||
            assignment.subject ||
            'فصل غير محدد',
          subject: assignment.subject || '',
          dueDate,
          totalPoints,
          targetCount: scopedStudentIds.length,
          submittedCount: studentRows.filter(row => row.submittedAt).length,
          missingCount: studentRows.filter(row => !row.submittedAt).length,
          lateCount: studentRows.filter(row => row.isLate).length,
          gradedCount: studentRows.filter(row => row.status === 'graded').length,
          submissionRate: percent(
            studentRows.filter(row => row.submittedAt).length,
            scopedStudentIds.length
          ),
          lateRate: percent(
            studentRows.filter(row => row.isLate).length,
            scopedStudentIds.length
          ),
          averageScorePercent: average(
            scoredRows.map(row => row.scorePercent || 0)
          ),
          minScorePercent:
            scoredRows.length > 0
              ? Math.min(...scoredRows.map(row => row.scorePercent || 0))
              : 0,
          maxScorePercent:
            scoredRows.length > 0
              ? Math.max(...scoredRows.map(row => row.scorePercent || 0))
              : 0,
          missingStudents: studentRows
            .filter(row => !row.submittedAt)
            .map(row => ({
              studentId: row.studentId,
              studentName: row.studentName,
            })),
          studentRows,
        };
      });
  }

  exportAssignmentAnalyticsToCSV(data: AssignmentAnalytics[]): string {
    const headers = [
      'الواجب',
      'الفصل',
      'المادة',
      'الطالب',
      'الحالة',
      'تاريخ التسليم',
      'متأخر',
      'الدرجة',
      'الدرجة العظمى',
      'النسبة',
      'التغذية الراجعة',
    ];
    const statusLabels: Record<AssignmentStudentReportRow['status'], string> = {
      missing: 'لم يسلم',
      submitted: 'مسلم',
      late: 'متأخر',
      graded: 'مقيم',
      returned: 'معاد للطالب',
    };
    const rows = data.flatMap(item =>
      item.studentRows.map(row => [
        row.assignmentTitle,
        row.className,
        row.subject,
        row.studentName,
        statusLabels[row.status],
        row.submittedAt,
        row.isLate ? 'نعم' : 'لا',
        row.grade,
        row.totalPoints,
        row.scorePercent,
        row.feedback,
      ])
    );

    return [headers, ...rows]
      .map(row => row.map(csvCell).join(','))
      .join('\n');
  }

  async exportToCSV(
    reportType: 'attendance' | 'grades' | 'performance',
    data: any[],
    filename: string
  ): Promise<string> {
    let csvContent = '';

    if (reportType === 'attendance' && data.length > 0) {
      csvContent =
        'اسم الطالب,إجمالي الحصص,الحصص المحضورة,معدل الحضور\n';
      data.forEach((item: AttendanceAnalytics) => {
        csvContent += `${item.studentName},${item.totalSessions},${item.attendedSessions},${item.attendanceRate}%\n`;
      });
    } else if (reportType === 'grades' && data.length > 0) {
      csvContent =
        'اسم الطالب,المادة,المعدل العام,أعلى درجة,أقل درجة\n';
      data.forEach((item: GradeAnalytics) => {
        csvContent += `${item.studentName},${item.subject || 'جميع المواد'},${item.averageGrade.toFixed(2)},${item.maxGrade.toFixed(2)},${item.minGrade.toFixed(2)}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }
}

const advancedReportsService = new AdvancedReportsService();

export default advancedReportsService;
